import React, { useEffect, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  CreditCard,
  ExternalLink,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  Calendar,
  Lock,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  useGetApiWorkspacesWorkspaceIdBillingStatus,
  getGetApiWorkspacesWorkspaceIdBillingStatusQueryKey,
  usePostApiWorkspacesWorkspaceIdBillingCreateCheckoutSession,
  usePostApiWorkspacesWorkspaceIdBillingCreatePortalSession,
} from '@/api/generated/billing/billing';
import { PricingCards } from '../components/PricingCards';
import { UsageMeter } from '../components/UsageMeter';
import { useBillingStore } from '@/stores/useBillingStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type {
  BillingStatusResponse,
  CreateCheckoutSessionResponse,
  CreatePortalSessionResponse,
} from '@/types';

export const BillingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loadingTierId, setLoadingTierId] = useState<string | null>(null);
  const { activeWorkspaceId } = useWorkspaceStore();

  const {
    plans,
    isLoadingPlans,
    plansError,
    fetchPlans,
    status: globalStatus,
    fetchStatus: fetchGlobalStatus,
  } = useBillingStore();

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Fetch real subscription status from backend scoped to active workspace
  const {
    data: statusRaw,
    isLoading: isLoadingStatus,
    isRefetching: isRefetchingStatus,
    isError: isErrorStatus,
    error: statusError,
    refetch: refetchStatus,
  } = useGetApiWorkspacesWorkspaceIdBillingStatus(activeWorkspaceId || '', {
    query: {
      enabled: !!activeWorkspaceId,
    },
  });

  const status = (statusRaw as unknown as BillingStatusResponse | undefined) || globalStatus;
  const planIdFromStatus = status?.planId?.toLowerCase();
  const isFreePlan = !status?.isSubscribed || planIdFromStatus === 'free';
  const isSubscribed = Boolean(status?.isSubscribed && planIdFromStatus !== 'free');
  const currentPlanId = planIdFromStatus || (isSubscribed ? 'pro' : 'free');
  const currentInterval = status?.interval || 'monthly';

  const [isProcessing, setIsProcessing] = useState(false);

  const isSuccess = searchParams.get('success') === 'true' || location.pathname.endsWith('/success');
  const isCanceled = searchParams.get('canceled') === 'true';

  // Handle Stripe redirect parameters or /billing/success path
  useEffect(() => {
    if (isSuccess && activeWorkspaceId) {
      setIsProcessing(true);
      let active = true;

      const checkStatus = async () => {
        if (!active) return;
        await fetchGlobalStatus(activeWorkspaceId);
        const updatedStatus = useBillingStore.getState().status;

        if (updatedStatus && updatedStatus.isSubscribed) {
          if (!active) return;
          setIsProcessing(false);
          toast.success('Assinatura ativada com sucesso! Bem-vindo ao seu plano atualizado.');
          queryClient.invalidateQueries({
            queryKey: getGetApiWorkspacesWorkspaceIdBillingStatusQueryKey(activeWorkspaceId),
          });
          if (location.pathname.endsWith('/success')) {
            navigate('/billing', { replace: true });
          } else {
            searchParams.delete('success');
            setSearchParams(searchParams, { replace: true });
          }
        } else {
          if (active) {
            setTimeout(checkStatus, 2000);
          }
        }
      };

      checkStatus();

      return () => {
        active = false;
      };
    } else if (isCanceled) {
      toast.info('O checkout foi cancelado. Você não foi cobrado.');
      searchParams.delete('canceled');
      setSearchParams(searchParams, { replace: true });
    }
  }, [isSuccess, isCanceled, activeWorkspaceId, fetchGlobalStatus, queryClient, location.pathname, navigate, searchParams, setSearchParams]);

  // Mutation to create Stripe Checkout session (workspace-scoped)
  const { mutate: createCheckoutSession, isPending: isCreatingCheckout } =
    usePostApiWorkspacesWorkspaceIdBillingCreateCheckoutSession({
      mutation: {
        onSuccess: (data) => {
          const response = data as unknown as CreateCheckoutSessionResponse;
          if (response?.checkoutUrl) {
            window.location.href = response.checkoutUrl;
          } else {
            toast.error('Não foi possível inicializar o checkout do Stripe. Por favor, tente novamente.');
            setLoadingTierId(null);
          }
        },
        onError: (error: any) => {
          setLoadingTierId(null);
          const errorMsg =
            error?.response?.data?.description ||
            error?.response?.data?.error ||
            'Falha ao iniciar a sessão de checkout. Por favor, tente novamente.';
          toast.error(errorMsg);
        },
      },
    });

  // Mutation to create Stripe Customer Portal session (workspace-scoped)
  const { mutate: createPortalSession, isPending: isOpeningPortal } =
    usePostApiWorkspacesWorkspaceIdBillingCreatePortalSession({
      mutation: {
        onSuccess: (data) => {
          const response = data as unknown as CreatePortalSessionResponse;
          if (response?.portalUrl) {
            window.location.href = response.portalUrl;
          } else {
            toast.error('Não foi possível abrir o portal de faturamento. Por favor, tente novamente.');
          }
        },
        onError: (error: any) => {
          const errorMsg =
            error?.response?.data?.description ||
            error?.response?.data?.error ||
            'Falha ao abrir o portal de faturamento. Por favor, tente novamente.';
          toast.error(errorMsg);
        },
      },
    });

  const handleSelectPlan = (tier: { id: string; name: string }, _isAnnual: boolean, priceId: string) => {
    if (!activeWorkspaceId) {
      toast.error('Nenhum workspace selecionado.');
      return;
    }
    setLoadingTierId(tier.id);
    createCheckoutSession({
      workspaceId: activeWorkspaceId,
      data: {
        priceId,
      },
    });
  };

  const handleOpenStripePortal = () => {
    if (!activeWorkspaceId) {
      toast.error('Nenhum workspace selecionado.');
      return;
    }
    if (isFreePlan) {
      scrollToPricing();
      return;
    }
    createPortalSession({
      workspaceId: activeWorkspaceId,
    });
  };

  const scrollToPricing = () => {
    const el = document.getElementById('pricing-plans');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isPaymentFailed = Boolean(
    (status as any)?.isPaymentFailed ||
    (status as any)?.paymentFailed ||
    (status as any)?.status === 'past_due' ||
    (status as any)?.subscriptionStatus === 'past_due'
  );

  const formatRenewalDate = (dateStr?: string | null) => {
    if (dateStr) {
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const isIso = typeof dateStr === 'string' && (dateStr.endsWith('Z') || dateStr.includes('T'));
          return new Intl.DateTimeFormat('pt-BR', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            ...(isIso ? { timeZone: 'UTC' } : {}),
          }).format(date);
        }
      } catch {
        // fallback
      }
    }
    // If backend doesn't provide a renewal timestamp, default to the next monthly cycle reset date
    const now = new Date();
    const nextCycle = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return new Intl.DateTimeFormat('pt-BR', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(nextCycle);
  };

  const formatBillingCycle = (renewalDateStr?: string | null, interval = 'monthly') => {
    if (!renewalDateStr) return 'Ciclo do mês corrente';
    try {
      const end = new Date(renewalDateStr);
      if (isNaN(end.getTime())) return 'Ciclo mensal';
      const start = new Date(end);
      if (interval === 'annual') {
        start.setFullYear(start.getFullYear() - 1);
      } else {
        start.setMonth(start.getMonth() - 1);
      }
      const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'short', day: 'numeric' });
      return `${formatter.format(start)} ➔ ${formatter.format(end)}`;
    } catch {
      return 'Ciclo mensal';
    }
  };

  const activeRenewalDate =
    status?.renewalDate ||
    (status as any)?.RenewalDate ||
    (status as any)?.renewal_date ||
    globalStatus?.renewalDate ||
    (globalStatus as any)?.RenewalDate;

  const activeNextQuotaResetDate =
    status?.nextQuotaResetDate ||
    status?.NextQuotaResetDate ||
    (status as any)?.next_quota_reset_date ||
    globalStatus?.nextQuotaResetDate ||
    globalStatus?.NextQuotaResetDate ||
    (globalStatus as any)?.next_quota_reset_date;

  const planDisplayName =
    status?.planName ||
    (currentPlanId === 'free'
      ? 'Gratuito'
      : currentPlanId === 'starter'
        ? 'Starter'
        : currentPlanId === 'enterprise'
          ? 'Enterprise'
          : 'Pro');

  const matchedPlan = plans?.find(
    (p) => p.planId?.toLowerCase() === currentPlanId?.toLowerCase()
  );
  const matchedPrice = matchedPlan?.prices?.find(
    (pr) =>
      pr.interval?.toLowerCase() === currentInterval?.toLowerCase() ||
      (currentInterval === 'annual' && (pr.interval?.toLowerCase() === 'annual' || pr.interval?.toLowerCase() === 'year')) ||
      (currentInterval === 'monthly' && (pr.interval?.toLowerCase() === 'monthly' || pr.interval?.toLowerCase() === 'month'))
  );

  const fallbackPriceAmount =
    currentPlanId === 'starter'
      ? currentInterval === 'annual'
        ? 490
        : 49
      : currentPlanId === 'enterprise'
        ? currentInterval === 'annual'
          ? 1490
          : 149
        : currentPlanId === 'pro'
          ? currentInterval === 'annual'
            ? 1490
            : 149
          : 0;

  const planPriceAmount = matchedPrice?.amount ?? fallbackPriceAmount;

  const totalQuotaLimit =
    status?.maxConversations ||
    (currentPlanId === 'free' ? 5 : currentPlanId === 'starter' ? 100 : currentPlanId === 'enterprise' ? 50000 : 500);

  if (isProcessing) {
    return (
      <div className="p-4 sm:p-6 space-y-8 max-w-6xl mx-auto flex flex-col items-center justify-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <h2 className="text-xl font-bold tracking-tight text-foreground">Ativando seus recursos Pro...</h2>
        <p className="text-sm text-muted-foreground">Aguardando confirmação do Stripe, por favor não recarregue.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-8 max-w-6xl mx-auto">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
            <CreditCard className="h-6 w-6 text-primary" />
            Assinatura e Faturamento
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {isSubscribed
              ? 'Gerencie sua assinatura ativa, cota de conversas com IA e preferências de faturamento.'
              : 'Escolha o plano ideal para escalar o suporte autônomo ao cliente em suas lojas.'}
          </p>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetchStatus()}
          disabled={isRefetchingStatus}
          className="self-start sm:self-auto h-8 text-xs text-muted-foreground hover:text-foreground gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetchingStatus ? 'animate-spin' : ''}`} />
          <span>Atualizar</span>
        </Button>
      </div>

      {/* Loading Skeleton */}
      {isLoadingStatus ? (
        <div className="space-y-6">
          <Skeleton className="h-44 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : isErrorStatus && !status ? (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center space-y-4 max-w-xl mx-auto my-8">
          <div className="h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">Falha ao Carregar Informações de Faturamento</h3>
            <p className="text-xs text-muted-foreground max-w-sm mx-auto">
              {(statusError as any)?.response?.data?.message ||
                (statusError as any)?.message ||
                'Não conseguimos recuperar o status da sua assinatura. Por favor, verifique sua conexão e tente novamente.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchStatus();
              fetchPlans();
            }}
            className="gap-2 text-xs"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span>Tentar Novamente</span>
          </Button>
        </div>
      ) : !isSubscribed ? (
        /* State A: Free User / Failed Payment (Pricing & Upgrade Flow via Stripe Checkout) */
        <div className="space-y-8">
          {/* Active Quota Usage Meter for Free / Past-Due User */}
          <section className="space-y-3">
            <UsageMeter
              usedConversations={status?.conversationsUsed ?? globalStatus?.conversationsUsed ?? 0}
              totalLimit={status?.maxConversations || globalStatus?.maxConversations || totalQuotaLimit}
              transferredConversations={
                status?.conversationsTransferred ?? globalStatus?.conversationsTransferred ?? 0
              }
              renewalDate={activeRenewalDate}
              nextQuotaResetDate={activeNextQuotaResetDate}
              planName={planDisplayName}
              isSubscribed={false}
              planId={currentPlanId}
              interval={currentInterval}
              isPaymentFailed={isPaymentFailed}
              onUpgradeClick={scrollToPricing}
            />
          </section>

          <div id="pricing-plans" className="text-center max-w-2xl mx-auto space-y-2 pt-2 scroll-mt-6">
            <Badge className="bg-primary/10 text-primary border-primary/20 text-xs font-semibold px-3 py-1">
              Planos Flexíveis
            </Badge>
            <h3 className="text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
              Escale o atendimento da sua loja
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Faça upgrade em poucos cliques. Todos os planos incluem chat com IA 24/7, respostas automáticas para dúvidas frequentes e transferência para sua equipe.
            </p>
          </div>

          <PricingCards
            plans={plans}
            isLoadingPlans={isLoadingPlans}
            plansError={plansError}
            onRetryPlans={fetchPlans}
            onSelectPlan={handleSelectPlan}
            loadingTierId={loadingTierId}
            isSubmitting={isCreatingCheckout}
            currentPlanId={currentPlanId}
          />

          {/* Security & Trust Footer */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-border/50">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-card/40 border border-border/40">
              <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-foreground">Pagamentos Seguros</p>
                <p className="text-muted-foreground">Criptografado e processado via Stripe</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-card/40 border border-border/40">
              <Sparkles className="h-5 w-5 text-primary shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-foreground">Liberação Imediata</p>
                <p className="text-muted-foreground">O limite de conversas é liberado assim que o pagamento for aprovado</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-card/40 border border-border/40">
              <Lock className="h-5 w-5 text-blue-500 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-foreground">Cancele a Qualquer Momento</p>
                <p className="text-muted-foreground">Cancele facilmente a qualquer momento direto pelo painel</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* State B: Paid User (Active Plan Management & Stripe Customer Portal) */
        <div className="space-y-6">
          {/* Subscription Overview Card */}
          <Card className="border-border/70 shadow-sm bg-card overflow-hidden">
            <CardHeader className="pb-4 bg-muted/10 border-b border-border/40">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5">
                    <CardTitle className="text-lg font-bold text-foreground">
                      {planDisplayName}
                    </CardTitle>
                    <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-xs font-semibold flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Assinatura Ativa
                    </Badge>
                  </div>
                  <CardDescription className="text-xs">
                    Sua assinatura está ativa e é gerenciada com segurança pelo Stripe.
                  </CardDescription>
                </div>

                {/* Stripe Customer Portal CTA - only shown for paid subscribers */}
                {!isFreePlan ? (
                  <Button
                    onClick={handleOpenStripePortal}
                    disabled={isOpeningPortal}
                    className="gap-2 text-xs font-semibold shadow-sm shrink-0"
                  >
                    {isOpeningPortal ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>Abrindo Portal...</span>
                      </>
                    ) : (
                      <>
                        <span>Gerenciar Assinatura</span>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={scrollToPricing}
                    className="gap-2 text-xs font-semibold shadow-sm shrink-0"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Fazer Upgrade</span>
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                <div className="p-3.5 rounded-lg bg-muted/30 border border-border/40 space-y-1">
                  <span className="text-muted-foreground">Valor do Faturamento</span>
                  <p className="text-base font-bold text-foreground">
                    R${planPriceAmount}{' '}
                    <span className="text-xs font-normal text-muted-foreground">
                      / {currentInterval === 'annual' ? 'ano ' : 'mês'}
                    </span>
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-muted/30 border border-border/40 space-y-1">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Próxima Data de Renovação
                  </span>
                  <p className="text-base font-bold text-foreground">
                    {formatRenewalDate(activeRenewalDate)}
                  </p>
                  <p className="text-[11px] text-muted-foreground font-medium">
                    Ciclo: {formatBillingCycle(activeRenewalDate, currentInterval)}
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-muted/30 border border-border/40 space-y-1">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3 text-emerald-500" /> Pagamento e Faturas
                  </span>
                  <p className="text-xs font-medium text-foreground">
                    Gerenciado com segurança via Stripe
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Atualizar formas de pagamento, visualizar recibos ou mudar de plano
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Quota Usage Meter */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Consumo de Conversas
            </h3>
            <UsageMeter
              usedConversations={status?.conversationsUsed ?? globalStatus?.conversationsUsed ?? 0}
              totalLimit={status?.maxConversations || globalStatus?.maxConversations || totalQuotaLimit}
              transferredConversations={
                status?.conversationsTransferred ?? globalStatus?.conversationsTransferred ?? 0
              }
              renewalDate={activeRenewalDate}
              nextQuotaResetDate={activeNextQuotaResetDate}
              planName={planDisplayName}
              isSubscribed={true}
              planId={currentPlanId}
              interval={currentInterval}
              isPaymentFailed={isPaymentFailed}
              onUpgradeClick={handleOpenStripePortal}
            />
          </section>

          {/* Plan Comparison & Upgrade for Paid Subscribers */}
          <div id="pricing-plans" className="text-center max-w-2xl mx-auto space-y-2 pt-6 scroll-mt-6 border-t border-border/50">
            <Badge className="bg-primary/10 text-primary border-primary/20 text-xs font-semibold px-3 py-1">
              Catálogo de Planos
            </Badge>
            <h3 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
              Compare e faça upgrade do seu plano
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Aumente sua cota de conversas e conecte mais lojas conforme seu negócio cresce.
            </p>
          </div>

          <PricingCards
            plans={plans}
            isLoadingPlans={isLoadingPlans}
            plansError={plansError}
            onRetryPlans={fetchPlans}
            onSelectPlan={handleSelectPlan}
            loadingTierId={loadingTierId}
            isSubmitting={isCreatingCheckout}
            currentPlanId={currentPlanId}
          />
        </div>
      )}
    </div>
  );
};

export default BillingPage;
